import React, { useEffect } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getUsersByID, getAllRanking } from "../../store/slices/user";
import useAuth from "../../../src/hooks/useAuth";
import styles from "./MyAccountTab.module.css";

// Import bank icons
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";

export default function MyAccountTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { singleUsers, playerBalance, wheelPoint, allRanking } = useSelector(
    (s) => s.user,
  );
  const account = Array.isArray(singleUsers) ? singleUsers[0] : null;

  // Determine user id (from current user slice, localStorage, or account)
  const storedId =
    user?.id || // prefer authenticated user id
    account?.id ||
    (typeof window !== "undefined" ? localStorage.getItem("userId") : null);

  useEffect(() => {
    dispatch(getAllRanking());
  }, [dispatch]);

  useEffect(() => {
    if (storedId) dispatch(getUsersByID({ id: storedId }));
  }, [storedId, dispatch]);

  // Helper function to get bank logo based on bank code
  const getBankLogo = (bankCode) => {
    switch (bankCode) {
      case "true":
        return truemoneyIcon;
      case "014":
        return scbIcon;
      case "006":
        return ktbIcon;
      case "004":
        return kbankIcon;
      case "002":
        return bblIcon;
      case "011":
        return ttbIcon;
      case "030":
        return gsbIcon;
      case "025":
        return krungsriIcon;
      case "034":
        return baacIcon;
      case "024":
        return uobIcon;
      case "033":
        return ghbIcon;
      case "022":
        return cimbIcon;
      case "066":
        return islamIcon;
      case "promptpay":
        return promptpayIcon;
      default:
        return scbIcon; // fallback
    }
  };

  // Helper function to get bank name based on bank code
  const getBankName = (bankCode) => {
    switch (bankCode) {
      case "true":
        return "ทรูมันนี่วอลเล็ท";
      case "014":
        return "ธนาคารไทยพาณิชย์";
      case "006":
        return "ธนาคารกรุงไทย";
      case "004":
        return "ธนาคารกสิกรไทย";
      case "002":
        return "ธนาคารกรุงเทพ";
      case "011":
        return "ธนาคารทีทีบี";
      case "030":
        return "ธนาคารออมสิน";
      case "025":
        return "ธนาคารกรุงศรี";
      case "034":
        return "ธนาคารธ.ก.ส.";
      case "024":
        return "ธนาคารUOB";
      case "033":
        return "ธนาคารอาคารสงเคราะห์";
      case "022":
        return "ธนาคารCIMB";
      case "066":
        return "ธนาคารอิสลาม";
      case "promptpay":
        return "พร้อมเพย์";
      default:
        return "ธนาคาร";
    }
  };

  // Map fields
  const apiBase =
    process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "";
  const username = account?.book_name || "-";
  const phone = account?.username || "-"; // username holds phone number per API
  const tempWallet = (account?.cashWallet ?? 0).toLocaleString() + " THB";
  const balanceValue =
    account?.creditWallet !== undefined
      ? account.creditWallet
      : Array.isArray(playerBalance)
        ? playerBalance[0]
        : 0;
  const balance = (balanceValue ?? 0).toLocaleString() + " THB";
  const gems = account?.wheelPoint ?? wheelPoint ?? 0;
  const bankName = account?.bank_name || "";
  const bankAccount = account?.book_number || "";
  const bankHolder = account?.book_name || "";

  // Determine rank index/class (prefer numeric rankClass from account)
  const currentClass = Number(
    account?.rankClass ?? account?.rankclass ?? account?.rank_class ?? 0,
  );

  // Resolve rank objects from allRanking (preferred) or fallback to account fields
  const currentRank =
    (allRanking && allRanking.length > 0 ? allRanking[currentClass] : null) ||
    (account?.rankImageURL || account?.rankName
      ? {
          rankImageURL: account?.rankImageURL,
          rankName: account?.rankName || account?.rankStatus,
          rankReceiveConditionAmount: account?.rankReceiveConditionAmount,
        }
      : null);

  const rankLabel =
    currentRank?.rankName || account?.rankName || account?.rankStatus || "-";

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h4 className={styles.title}>👤 บัญชีของฉัน</h4>
        <p className={styles.subtitle}>ข้อมูลส่วนตัวและรายละเอียดบัญชี</p>
      </div>

      {/* Main Profile Card */}
      <div className={styles.profileCard}>
        <div className={styles.profileInfo}>
          <div className={styles.avatarSection}>
            <div className={styles.avatar}>
              <Icon icon="mdi:account" width={48} height={48} />
            </div>
            <div className={styles.userInfo}>
              <h3 className={styles.displayName}>{username}</h3>
              <p className={styles.phoneNumber}>{phone}</p>
            </div>
          </div>
        </div>
        <div className={styles.rankBadge}>
          <div className={styles.rankHeader}>
            {currentRank?.rankImageURL ? (
              <img
                className={styles.rankBadgeSmall}
                src={`${apiBase}/uploads${currentRank.rankImageURL}`}
                alt={currentRank?.rankName || "rank"}
                loading="lazy"
              />
            ) : (
              <div className={styles.rankBadgePlaceholderSmall}>🏅</div>
            )}
            <div className={styles.rankInfo}>
              <div className={styles.rankName}>
                {currentRank?.rankName || rankLabel || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <Icon icon="solar:wallet-2-bold" width={28} height={28} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>กระเป๋าเงินสด</div>
            <div className={styles.statValue}>{tempWallet}</div>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.primaryStat}`}>
          <div className={styles.statIcon}>
            <Icon icon="tabler:coin-filled" width={28} height={28} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>เครดิตปัจจุบัน</div>
            <div className={styles.statValue}>{balance}</div>
          </div>
        </div>

        {/*<div className={`${styles.statCard} ${styles.gemStat}`}>
          <div className={styles.statIcon}>
            <Icon icon="mdi:diamond" width={24} height={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>เพชรสะสม</div>
            <div className={styles.statValue}>
              {gems}
              <Icon icon="mdi:diamond" className={styles.gemIcon} />
            </div>
          </div>
        </div>*/}
      </div>

      {/* Bank Information Card */}
      <div className={styles.bankCard}>
        <div className={styles.bankHeader}>
          <div className={styles.bankTitle}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ color: "black" }}>ข้อมูลธนาคาร</span>
          </div>
        </div>

        {bankAccount ? (
          <div className={styles.bankInfo}>
            <div className={styles.bankLogoSection}>
              <div className={styles.bankLogoWrapper}>
                <img
                  src={getBankLogo(bankName)}
                  alt={getBankName(bankName)}
                  className={styles.bankLogo}
                />
              </div>
              <div className={styles.bankDetails}>
                <h4 className={styles.bankNameText}>{getBankName(bankName)}</h4>
                <p className={styles.bankAccountNumber}>{bankAccount}</p>
                {bankHolder && (
                  <p className={styles.bankHolderName}>
                    ชื่อบัญชี: {bankHolder}
                  </p>
                )}
              </div>
            </div>
            <div className={styles.bankStatus}>
              <div className={styles.statusBadge}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>ยืนยันแล้ว</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.bankPlaceholder}>
            <div className={styles.placeholderIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.placeholderText}>
              <h4>ยังไม่มีบัญชีธนาคาร</h4>
              <p>กรุณาเพิ่มข้อมูลบัญชีธนาคารเพื่อทำธุรกรรม</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
